import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [currentChapter, setCurrentChapter] = useState<number | null>(null);
  const [showFullDesc, setShowFullDesc] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentChapter(null);
    setShowFullDesc(false);
  }, [id]);

  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <main className="pt-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的漫画不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = comics.filter(
    (c) => c.categorySlug === comic.categorySlug && c.id !== comic.id
  ).slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    url: `https://hanmanwang.com/comic/${comic.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="pt-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />
        </div>

        <section className="bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 pb-14">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10 animate-fadeInUp">
              <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px]">
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-4">
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>
                    {comic.author}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-md text-xs font-medium ${
                    comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-accent-500"></i></span>
                    {comic.rating} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                    {(comic.totalViews / 10000).toFixed(0)} 万阅读
                  </span>
                  <span>{comic.totalChapters} 话</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-heading font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className={`text-sm text-foreground-600 leading-relaxed ${showFullDesc ? '' : 'line-clamp-3'}`}>
                    {comic.description}
                  </p>
                  {comic.description.length > 150 && (
                    <button
                      onClick={() => setShowFullDesc(!showFullDesc)}
                      className="text-sm text-primary-600 hover:text-primary-700 mt-1 cursor-pointer"
                    >
                      {showFullDesc ? '收起' : '展开全部'}
                    </button>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      if (comic.chapters.length > 0) {
                        setCurrentChapter(comic.chapters[comic.chapters.length - 1].chapterNumber);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                  </button>
                  <button
                    className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap border border-background-200/70"
                  >
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14" id="chapters">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-6">
              章节目录
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.totalChapters} 话 · 最近更新 {comic.updatedAt}
              </span>
            </h2>

            {currentChapter ? (
              <div className="animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground-800">
                    第 {currentChapter} 话
                  </h3>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const idx = comic.chapters.findIndex((ch) => ch.chapterNumber === currentChapter);
                        if (idx > 0) {
                          setCurrentChapter(comic.chapters[idx - 1].chapterNumber);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      disabled={currentChapter <= 1}
                      className="px-4 py-2 bg-background-50 text-foreground-700 rounded-lg text-sm font-medium border border-background-200/70 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      上一话
                    </button>
                    <button
                      onClick={() => setCurrentChapter(null)}
                      className="px-4 py-2 bg-background-50 text-foreground-700 rounded-lg text-sm font-medium border border-background-200/70 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      返回目录
                    </button>
                    <button
                      onClick={() => {
                        const idx = comic.chapters.findIndex((ch) => ch.chapterNumber === currentChapter);
                        if (idx < comic.chapters.length - 1) {
                          setCurrentChapter(comic.chapters[idx + 1].chapterNumber);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      disabled={currentChapter >= comic.chapters.length}
                      className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      下一话
                    </button>
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-10 min-h-[600px] flex items-center justify-center">
                  <div className="text-center">
                    <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-100">
                      <i className="ri-book-open-line text-3xl text-primary-600"></i>
                    </span>
                    <h4 className="text-lg font-heading font-semibold text-foreground-800 mb-2">
                      {comic.title} - 第 {currentChapter} 话
                    </h4>
                    <p className="text-sm text-foreground-500">
                      漫画内容区域 — 在实际部署中，此处将展示漫画的图片内容，支持下拉式滚动阅读体验。
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {[...comic.chapters].reverse().map((chapter) => (
                  <button
                    key={chapter.id}
                    onClick={() => {
                      setCurrentChapter(chapter.chapterNumber);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="text-left px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer group"
                  >
                    <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors mb-1">
                      第 {chapter.chapterNumber} 话
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{chapter.updatedAt}</span>
                      <span className="flex items-center gap-0.5">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                        {(chapter.viewCount / 1000).toFixed(1)}k
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="bg-background-50 py-10 md:py-14">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2.5">
                        <div className="flex items-center gap-0.5 text-background-50 text-xs">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-accent-500"></i></span>
                          <span>{rc.rating}</span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}